import React, { useState } from 'react';
import { FERTILITY_PRODUCTS } from '../data';
import { NovaUnifiedPageBar } from '../components/NovaUnified';
import { FERTILITY_DEMO_SECTION_BORDER, demoChapterStackColumnStyle } from '../designSystem';

const FONT_BODY = "'Plus Jakarta Sans', -apple-system, sans-serif";

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'ovulation', label: 'Ovulation' },
  { key: 'supplements', label: 'Supplements' },
  { key: 'tracking', label: 'Tracking' },
];

function getCategoryKey(category) {
  return (category || '').toLowerCase();
}

function getTagStyle(category) {
  const k = getCategoryKey(category);
  if (k === 'tracking') return { bg: 'var(--sage-l)', color: 'var(--sage-d)', border: 'var(--sage-m)' };
  if (k === 'supplements') return { bg: '#fdf3e0', color: '#92550a', border: '#f5c97a' };
  return { bg: 'var(--pink-l)', color: 'var(--pink-d)', border: 'var(--pink-m)' };
}

export default function FertilityDemoProducts({ navigate, m, demoChapter: _demoChapter = 'fertility', omitNovaPicksBar = false }) {
  const productCardBorder = _demoChapter === 'fertility' ? FERTILITY_DEMO_SECTION_BORDER : '1px solid var(--border)';
  const [filter, setFilter] = useState('all');
  const products = (FERTILITY_PRODUCTS || []).slice(0, 6);
  const filtered = filter === 'all' ? products : products.filter((p) => getCategoryKey(p.category) === filter);
  const topPick = products[0]; // Easy@Home has highest digs

  return (
    <div style={{ fontFamily: FONT_BODY, width: '100%', boxSizing: 'border-box', ...demoChapterStackColumnStyle }}>
      {!omitNovaPicksBar && (
        <div>
          <NovaUnifiedPageBar
            layout="stacked"
            contextPill="Guidance"
            body={
              <div style={{ fontSize: 13, color: '#4b1528', lineHeight: 1.55 }}>
                <strong>Nova&apos;s picks for stim day 8 —</strong> Based on your cycle, these are the products women like you reach for most during the stim phase.
              </div>
            }
            actionLabel="Ask Nova about products →"
            onAction={() => navigate('/nova')}
          />
        </div>
      )}

      {/* FILTER BAR */}
      <div className="card-label" style={{ flexWrap: 'wrap', gap: 8 }}>
        <span>Filter</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              style={{
                fontFamily: FONT_BODY,
                fontSize: 12,
                fontWeight: 600,
                padding: '5px 14px',
                borderRadius: 999,
                border: `1px solid ${filter === f.key ? 'var(--pink-m)' : 'var(--border)'}`,
                background: filter === f.key ? 'var(--pink-l)' : 'var(--sand)',
                color: filter === f.key ? 'var(--pink-dk)' : 'var(--muted)',
                cursor: 'pointer',
                textTransform: 'none',
                letterSpacing: 0,
              }}
            >
              {f.label}
            </button>
          ))}
          <select
            style={{
              marginLeft: 'auto',
              fontFamily: FONT_BODY,
              fontSize: 12,
              fontWeight: 500,
              color: 'var(--muted)',
              background: 'var(--sand)',
              border: '1px solid var(--border)',
              borderRadius: 999,
              padding: '5px 12px',
              cursor: 'pointer',
            }}
          >
            <option>Most popular</option>
            <option>Highest rated</option>
            <option>Price: low to high</option>
            <option>Price: high to low</option>
          </select>
        </div>
      </div>

      {/* PRODUCT GRID */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: m ? '1fr' : 'repeat(3, 1fr)',
          gap: 10,
        }}
      >
        {filtered.map((p) => {
          const isFeatured = p.id === topPick?.id;
          const tagStyle = getTagStyle(p.category);
          return (
            <article
              key={p.id}
              onClick={() => navigate('/signup')}
              style={{
                background: 'var(--white)',
                border: isFeatured ? '2px solid var(--pink)' : productCardBorder,
                borderRadius: 14,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                position: 'relative',
              }}
            >
              {isFeatured && (
                <div
                  style={{
                    position: 'absolute',
                    top: -1,
                    left: 14,
                    background: 'var(--pink)',
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    padding: '3px 10px',
                    borderRadius: '0 0 8px 8px',
                    zIndex: 1,
                  }}
                >
                  Nova&apos;s top pick
                </div>
              )}
              <div style={{ position: 'relative', height: 140, background: 'var(--sand)', overflow: 'hidden', flexShrink: 0 }}>
                {p.img ? (
                  <img src={p.img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                ) : (
                  <div style={{ width: '100%', height: '100%', background: '#e8e2d8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36 }}>💊</div>
                )}
                <div
                  style={{
                    position: 'absolute',
                    top: 10,
                    left: 10,
                    background: 'rgba(255, 253, 249, 0.92)',
                    borderRadius: 999,
                    padding: '4px 9px',
                    fontSize: 11,
                    fontWeight: 700,
                    color: 'var(--text)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <span style={{ color: '#f59e0b', fontSize: 11 }}>★</span> {p.rating}{' '}
                  <span style={{ fontSize: 10, fontWeight: 500, color: 'var(--muted)' }}>({p.reviews >= 1000 ? `${(p.reviews / 1000).toFixed(1)}k` : p.reviews})</span>
                </div>
                <div
                  style={{
                    position: 'absolute',
                    bottom: 10,
                    right: 10,
                    background: 'var(--pink)',
                    color: '#ffffff',
                    fontSize: 12,
                    fontWeight: 800,
                    padding: '6px 12px',
                    borderRadius: 999,
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {p.digs} digs
                </div>
              </div>
              <div style={{ padding: '12px 14px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div className="card-label" style={{ marginBottom: 3 }}>
                  <span>{p.brand}</span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', lineHeight: 1.3, marginBottom: 4 }}>{p.name}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.45, flex: 1, marginBottom: 10 }}>{p.excerpt}</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto' }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.01em' }}>${p.price}</div>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      padding: '4px 10px',
                      borderRadius: 999,
                      background: tagStyle.bg,
                      color: tagStyle.color,
                      border: `1px solid ${tagStyle.border}`,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {p.category}
                  </span>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {/* CTA BAR */}
      <div
        style={{
          background: 'var(--pink)',
          borderRadius: 14,
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
        }}
      >
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#ffffff' }}>Sign up to access the full catalog</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)', marginTop: 2 }}>200+ fertility products — curated, reviewed, and matched to your cycle</div>
        </div>
        <button
          type="button"
          onClick={() => navigate('/signup')}
          style={{
            fontFamily: FONT_BODY,
            fontSize: 12,
            fontWeight: 700,
            color: 'var(--pink-dk)',
            background: 'var(--white)',
            border: 'none',
            borderRadius: 999,
            padding: '8px 20px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          Sign up free →
        </button>
      </div>
    </div>
  );
}
